import {
  ControlDeviceTableBody,
  ControlDeviceTableHeader,
  ControlDeviceTablePlaceholder,
  DetectBrowserDevice,
  DeviceWithInstalledPower,
  LineChartBattery,
  ProductInput,
} from '@e2m-customer-portal/domains';
import { ForecastSlice, MarketLocation, useLocale, useTokenInfo } from '@e2m-customer-portal/hooks';
import { getInputFieldTypes, InputFieldType, isBattery, UserRole } from '@e2m-customer-portal/shared';
import { DowntimeRevokeModal } from '@e2m-customer-portal/ui';
import { ErrorInfoIcon } from 'assets/icons';
import clsx from 'clsx';
import { endOfDay, Interval, isBefore, isToday, isWeekend } from 'date-fns';
import { ForecastProperty, ForecastTarget } from 'generated/graphql';
import React, { ReactElement, Suspense, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface ControlDeviceTableProps {
  marketLocation: MarketLocation;
  deviceIndex: number;
  device: DeviceWithInstalledPower;
  deadlineDate?: Date;
  slices: readonly ForecastSlice[];
  interval: Interval;
  daysInForecast: Date[];
  readOnly?: boolean;
  resetTrigger?: boolean;
  isBeingEdited: boolean;
  overviewMap: Map<string, ProductInput[][][]>;
  editedSliceProducts: Map<string, Set<InputFieldType>>;
  editedDevices: string[];
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, ProductInput[][][]>>>;
  setEditedSliceProducts: React.Dispatch<React.SetStateAction<Map<string, Set<InputFieldType>>>>;
  setErroneousSliceProducts: React.Dispatch<React.SetStateAction<Map<string, Set<InputFieldType>>>>;
  saveChanges: () => void;
  currentForecastProperty?: ForecastProperty;
}

export function ControlDeviceTable({
  marketLocation,
  deviceIndex,
  device,
  deadlineDate,
  slices,
  interval,
  daysInForecast,
  readOnly,
  resetTrigger,
  isBeingEdited,
  overviewMap,
  editedSliceProducts,
  editedDevices,
  setOverviewMap,
  setEditedSliceProducts,
  setErroneousSliceProducts,
  saveChanges,
  currentForecastProperty,
}: Readonly<ControlDeviceTableProps>): ReactElement<ControlDeviceTableProps> {
  const [t] = useTranslation();
  const products = isBattery(marketLocation.technologies)
    ? getInputFieldTypes(device.products, 'bands').filter(
        (field) => ![InputFieldType.AFRR_NEGATIVE, InputFieldType.AFRR_POSITIVE].includes(field),
      )
    : getInputFieldTypes(device.products);
  const { code: locale } = useLocale();

  const { roles } = useTokenInfo();
  const isOptimizedBattery =
    isBattery(marketLocation.technologies) &&
    currentForecastProperty?.target === ForecastTarget.VPP &&
    currentForecastProperty.editableMarkets.length === 0;

  const isReadOnlyBattery = isBattery(marketLocation.technologies) && !roles.has(UserRole.VPPOperator);

  const [isExpanded, setIsExpanded] = useState(deviceIndex === 0);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<InputFieldType>>(
    isReadOnlyBattery ? new Set() : new Set(products),
  );
  const [revokeDowntimeInterval, setRevokeDowntimeInterval] = useState<Interval | null>(null);
  const isMobile = DetectBrowserDevice() === 'Mobile';

  const handleClick = () => {
    if (editedDevices.includes(device.id)) {
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 1500);
      const deviceMap = overviewMap.get(device.id);
      if (deviceMap)
        deviceMap[Math.floor(deviceMap.length / 2)][0][Math.floor(slices[0].fields.length / 2)].input?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      <DowntimeRevokeModal
        device={device}
        currentForecastProperty={currentForecastProperty}
        revokeDowntimeInterval={revokeDowntimeInterval}
        setRevokeDowntimeInterval={setRevokeDowntimeInterval}
      />
      {isOptimizedBattery && (
        <LineChartBattery
          device={device}
          interval={interval}
          marketLocationName={marketLocation.externalName}
          isMobile={isMobile}
        />
      )}
      <ControlDeviceTableHeader
        marketLocation={marketLocation}
        device={device}
        interval={interval}
        products={products}
        selectedProducts={selectedProducts}
        open={isExpanded}
        disableAssistant={isBeingEdited || !products.length}
        disabled={editedDevices.includes(device.id)}
        onClick={handleClick}
        setSelectedProducts={setSelectedProducts}
        isReadOnlyBattery={isReadOnlyBattery}
        isOptimizedBattery={isOptimizedBattery}
      />
      {isExpanded && products.length ? (
        <table className="border-spacing-0 bg-white [container:table_/_size]" data-testid={device.name}>
          <colgroup>
            <col span={2} />
            {daysInForecast.map((day) => (
              <col
                key={`colgroup-${device.id}-${day.getDate()}`}
                style={{ width: `${100 / daysInForecast.length}cqw` }}
              />
            ))}
          </colgroup>
          <thead data-testid="device-table-head">
            <tr>
              <th
                className="border-0 border-b border-solid border-divider px-6 text-start text-sm font-normal leading-4"
                colSpan={2}
              >
                {t('MARKET_LOCATION_FORECAST_TABLE_DATE')}
              </th>
              {daysInForecast.map((date) => {
                const isAfterDeadline = deadlineDate && isBefore(endOfDay(date), deadlineDate);
                return (
                  <th
                    key={`date-${device.id}-${date.toISOString()}`}
                    className={clsx(
                      'border-0 border-b border-l border-solid border-divider p-0 text-sm font-normal leading-4',
                      {
                        'bg-blue-grey-200/5': isWeekend(date),
                        'text-black text-opacity-60': isAfterDeadline,
                      },
                    )}
                  >
                    <div className={clsx('w-full h-full py-2', { 'bg-green-50/40': isToday(date) })}>
                      {date
                        .toLocaleDateString(locale, {
                          weekday: 'short',
                          day: '2-digit',
                          month: '2-digit',
                        })
                        .replace(/(?<=[A-Za-z])\./, '')}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <Suspense fallback={<ControlDeviceTablePlaceholder daysInForecast={daysInForecast} products={products} />}>
            <ControlDeviceTableBody
              marketLocation={marketLocation}
              device={device}
              deadlineDate={deadlineDate}
              selectedProducts={selectedProducts}
              slices={slices}
              interval={interval}
              daysInForecast={daysInForecast}
              readOnly={readOnly || isReadOnlyBattery}
              resetTrigger={resetTrigger}
              overviewMap={overviewMap}
              editedSliceProducts={editedSliceProducts}
              isHighlighted={isHighlighted}
              setOverviewMap={setOverviewMap}
              setEditedSliceProducts={setEditedSliceProducts}
              setErroneousSliceProducts={setErroneousSliceProducts}
              setIsHighlighted={setIsHighlighted}
              setRevokeDowntimeInterval={setRevokeDowntimeInterval}
              saveChanges={saveChanges}
            />
          </Suspense>
        </table>
      ) : (
        isExpanded && (
          <div className="flex justify-center">
            <div className="m-4 max-w-fit flex-col items-center gap-y-4 rounded-lg border border-solid border-error-50 p-4">
              <div className="my-0 flex max-w-[60ch] items-start gap-x-2">
                <ErrorInfoIcon className="mt-2.5 w-12 min-w-12 text-error-500 sm:w-5 sm:min-w-5" />
                <div>
                  <Trans
                    components={{
                      h1: <h1 className="mb-1.5 text-base text-error-500">{}</h1>,
                      p: <p className="mt-0.5" />,
                      mail: <a href={`mailto:${t('CONTACT_INFO_CONTROL_CENTER_MAIL')}`}>{}</a>,
                      phone: <a href={`tel:${t('CONTACT_INFO_CONTROL_CENTER_PHONE')}`}>{}</a>,
                    }}
                  >
                    {t('FORECAST_CONTROL_ENERGY_NO_PRODUCTS', {
                      mail: t('CONTACT_INFO_CONTROL_CENTER_MAIL'),
                      phone: t('CONTACT_INFO_CONTROL_CENTER_PHONE'),
                    })}
                  </Trans>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
}
